import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  BookOpen, ArrowLeft, Clock, MapPin, User, Star, Users, 
  CheckCircle, Lightbulb, Target, GraduationCap, UserCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// KG Subject Data
const kgSubjectsData = {
  literacy: {
    id: "pre-1",
    name: "Literacy",
    color: "from-pink-500 to-rose-400",
    icon: "📚",
    room: "KG Block A",
    schedule: "Mon-Fri 8:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Foundation in letters, phonics, and reading through play-based learning and interactive activities. Our literacy program is designed to spark a love for reading and writing in young learners.",
    fullDescription: "The Literacy program at Young Christian Preparatory School provides a comprehensive foundation for reading and writing. Through engaging, play-based activities, children develop essential pre-reading and early reading skills. Our approach combines phonics instruction with whole language experiences, ensuring students build strong literacy foundations.",
    objectives: [
      "Learn alphabet recognition (uppercase and lowercase)",
      "Develop phonics skills and sound-letter relationships",
      "Begin reading simple words and short sentences",
      "Practice writing letters and simple words",
      "Build vocabulary through stories and conversations",
      "Develop listening and comprehension skills"
    ],
    activities: [
      "Letter tracing and formation exercises",
      "Phonics songs and rhymes",
      "Story time with picture books",
      "Word games and puzzles",
      "Show and tell sessions",
      "Interactive reading circles"
    ],
    materials: [
      "Alphabet charts and flashcards",
      "Picture books and story collections",
      "Writing practice workbooks",
      "Letter blocks and manipulatives",
      "Phonics games and activities"
    ],
    rating: 4.9,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "https://pbskids.org/" },
      { name: "Resource 2", url: "https://www.education.com/game/pancake-fractions/" },
      { name: "Resource 3", url: "https://www.abcmouse.com/abc/" },
      { name: "Resource 4", url: "https://monkeypen.com/pages/free-childrens-books" },
      { name: "Resource 5", url: "https://www.starfall.com/h/index.php" },
      { name: "Resource 6", url: "https://www.abcya.com/grades/k" },
      { name: "Resource 7", url: "https://www.teachyourmonster.org/teach-your-monster-to-read/" },
      { name: "Resource 8", url: "#literacy-8" },
      { name: "Resource 9", url: "#literacy-9" },
      { name: "Resource 10", url: "#literacy-10" },
      { name: "Resource 11", url: "#literacy-11" },
      { name: "Resource 12", url: "#literacy-12" },
      { name: "Resource 13", url: "#literacy-13" },
      { name: "Resource 14", url: "#literacy-14" },
      { name: "Resource 15", url: "#literacy-15" },
      { name: "Resource 16", url: "#literacy-16" },
      { name: "Resource 17", url: "#literacy-17" },
      { name: "Resource 18", url: "#literacy-18" },
      { name: "Resource 19", url: "#literacy-19" },
      { name: "Resource 20", url: "#literacy-20" }
    ]
  },
  numeracy: {
    id: "pre-2",
    name: "Numeracy",
    color: "from-blue-500 to-cyan-400",
    icon: "🔢",
    room: "KG Block A",
    schedule: "Mon-Fri 9:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Introduction to counting, numbers, and basic math concepts through fun activities.",
    fullDescription: "Our Numeracy program introduces young learners to the exciting world of numbers and mathematics. Through hands-on activities, games, and real-world connections, children develop number sense and foundational math skills that prepare them for future mathematical learning.",
    objectives: [
      "Learn to count from 1 to 100",
      "Recognize and write numbers 0-20",
      "Understand basic math concepts (more/less, big/small)",
      "Develop early addition and subtraction skills",
      "Learn shapes and patterns",
      "Understand measurement concepts"
    ],
    activities: [
      "Counting games with manipulatives",
      "Number songs and rhymes",
      "Shape hunts and sorting activities",
      "Pattern creation with blocks and beads",
      "Math storytelling",
      "Measurement activities with non-standard units"
    ],
    materials: [
      "Number charts and flashcards",
      "Counting manipulatives (blocks, beads, counters)",
      "Shape puzzles and games",
      "Math workbooks",
      "Measuring tools"
    ],
    rating: 4.9,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "https://www.khanacademy.org/kids" },
      { name: "Resource 2", url: "https://www.worksheetfun.com/preschool-worksheets/" },
      { name: "Resource 3", url: "https://www.worksheetfun.com/preschool-worksheets/" },
      { name: "Resource 4", url: "#numeracy-4" },
      { name: "Resource 5", url: "#numeracy-5" },
      { name: "Resource 6", url: "#numeracy-6" },
      { name: "Resource 7", url: "#numeracy-7" },
      { name: "Resource 8", url: "#numeracy-8" },
      { name: "Resource 9", url: "#numeracy-9" },
      { name: "Resource 10", url: "#numeracy-10" },
      { name: "Resource 11", url: "#numeracy-11" },
      { name: "Resource 12", url: "#numeracy-12" },
      { name: "Resource 13", url: "#numeracy-13" },
      { name: "Resource 14", url: "#numeracy-14" },
      { name: "Resource 15", url: "#numeracy-15" },
      { name: "Resource 16", url: "#numeracy-16" },
      { name: "Resource 17", url: "#numeracy-17" },
      { name: "Resource 18", url: "#numeracy-18" },
      { name: "Resource 19", url: "#numeracy-19" },
      { name: "Resource 20", url: "#numeracy-20" }
    ]
  },
  "social-studies": {
    id: "pre-3",
    name: "Social Studies",
    color: "from-green-500 to-emerald-400",
    icon: "🌍",
    room: "KG Block B",
    schedule: "Mon/Wed/Fri 10:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Explore nature, animals, plants, and Our World Our People (OWOP) through hands-on discovery.",
    fullDescription: "Social Studies in KG introduces children to the world around them through the Our World Our People (OWOP) curriculum. Students explore nature, community, and Ghanaian culture while developing awareness of their environment and respect for others.",
    objectives: [
      "Learn about animals and their habitats",
      "Discover plants and how they grow",
      "Understand family and community roles",
      "Appreciate Ghanaian culture and traditions",
      "Develop environmental awareness",
      "Learn about helpers in our community"
    ],
    activities: [
      "Nature walks and observations",
      "Community helper dress-up days",
      "Cultural celebration activities",
      "Plant growing projects",
      "Animal study with pictures and videos",
      "Family tree projects"
    ],
    materials: [
      "Picture books about nature and community",
      "Cultural artifacts and items",
      "Nature study kits",
      "Community helper props",
      "Maps and globes"
    ],
    rating: 4.8,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "https://curriculumresources.edu.gh/year1_social-studies/" },
      { name: "Resource 2", url: "https://veggietales.com" },
      { name: "Resource 3", url: "#social-studies-3" },
      { name: "Resource 4", url: "#social-studies-4" },
      { name: "Resource 5", url: "#social-studies-5" },
      { name: "Resource 6", url: "#social-studies-6" },
      { name: "Resource 7", url: "#social-studies-7" },
      { name: "Resource 8", url: "#social-studies-8" },
      { name: "Resource 9", url: "#social-studies-9" },
      { name: "Resource 10", url: "#social-studies-10" },
      { name: "Resource 11", url: "#social-studies-11" },
      { name: "Resource 12", url: "#social-studies-12" },
      { name: "Resource 13", url: "#social-studies-13" },
      { name: "Resource 14", url: "#social-studies-14" },
      { name: "Resource 15", url: "#social-studies-15" },
      { name: "Resource 16", url: "#social-studies-16" },
      { name: "Resource 17", url: "#social-studies-17" },
      { name: "Resource 18", url: "#social-studies-18" },
      { name: "Resource 19", url: "#social-studies-19" },
      { name: "Resource 20", url: "#social-studies-20" }
    ]
  },
  "computer-science": {
    id: "pre-4",
    name: "Computer Science and Engineering",
    color: "from-purple-500 to-violet-400",
    icon: "💻",
    room: "STEM Room",
    schedule: "Tue/Thu 10:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Interactive games, building, STEM activities, and problem solving. Fun learning activities designed to build young minds.",
    fullDescription: "Our Computer Science and Engineering program introduces young learners to technology and engineering concepts through age-appropriate, hands-on activities. Children develop problem-solving skills, creativity, and digital literacy in a fun and engaging environment.",
    objectives: [
      "Learn basic computer skills (mouse, keyboard)",
      "Develop problem-solving abilities",
      "Build structures with various materials",
      "Understand simple coding concepts through games",
      "Foster creativity and innovation",
      "Learn to work in teams on projects"
    ],
    activities: [
      "Building challenges with blocks and LEGO",
      "Simple coding games (unplugged and digital)",
      "Robot play and introduction",
      "Design and build projects",
      "Interactive educational software",
      "Team problem-solving challenges"
    ],
    materials: [
      "Building blocks and LEGO sets",
      "Age-appropriate tablets/computers",
      "Educational coding games",
      "Engineering challenge kits",
      "Robots for exploration"
    ],
    rating: 4.8,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "https://www.adaptedmind.com/Math-Worksheets.html" },
      { name: "Resource 2", url: "#computer-science-2" },
      { name: "Resource 3", url: "#computer-science-3" },
      { name: "Resource 4", url: "#computer-science-4" },
      { name: "Resource 5", url: "#computer-science-5" },
      { name: "Resource 6", url: "#computer-science-6" },
      { name: "Resource 7", url: "#computer-science-7" },
      { name: "Resource 8", url: "#computer-science-8" },
      { name: "Resource 9", url: "#computer-science-9" },
      { name: "Resource 10", url: "#computer-science-10" },
      { name: "Resource 11", url: "#computer-science-11" },
      { name: "Resource 12", url: "#computer-science-12" },
      { name: "Resource 13", url: "#computer-science-13" },
      { name: "Resource 14", url: "#computer-science-14" },
      { name: "Resource 15", url: "#computer-science-15" },
      { name: "Resource 16", url: "#computer-science-16" },
      { name: "Resource 17", url: "#computer-science-17" },
      { name: "Resource 18", url: "#computer-science-18" },
      { name: "Resource 19", url: "#computer-science-19" },
      { name: "Resource 20", url: "#computer-science-20" }
    ]
  },
  "creative-arts": {
    id: "pre-5",
    name: "Creative Arts",
    color: "from-orange-500 to-amber-400",
    icon: "🎨",
    room: "Art Room",
    schedule: "Fri 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Express creativity through drawing, painting, music, movement, and drama.",
    fullDescription: "The Creative Arts program nurtures children's natural creativity and self-expression. Through visual arts, music, movement, and drama, students develop fine motor skills, creativity, and confidence while exploring various artistic mediums.",
    objectives: [
      "Explore various art materials and techniques",
      "Learn songs, rhymes, and musical concepts",
      "Develop fine motor skills through art activities",
      "Express creativity freely and confidently",
      "Participate in dramatic play and performances",
      "Appreciate different forms of art and music"
    ],
    activities: [
      "Drawing and painting sessions",
      "Craft projects with various materials",
      "Music and movement activities",
      "Dramatic play and role-playing",
      "Dance and rhythm activities",
      "Art exhibitions and performances"
    ],
    materials: [
      "Crayons, markers, and colored pencils",
      "Paints and brushes",
      "Craft supplies (paper, glue, scissors)",
      "Musical instruments",
      "Dress-up clothes and props"
    ],
    rating: 4.9,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "#creative-arts-1" },
      { name: "Resource 2", url: "#creative-arts-2" },
      { name: "Resource 3", url: "#creative-arts-3" },
      { name: "Resource 4", url: "#creative-arts-4" },
      { name: "Resource 5", url: "#creative-arts-5" },
      { name: "Resource 6", url: "#creative-arts-6" },
      { name: "Resource 7", url: "#creative-arts-7" },
      { name: "Resource 8", url: "#creative-arts-8" },
      { name: "Resource 9", url: "#creative-arts-9" },
      { name: "Resource 10", url: "#creative-arts-10" },
      { name: "Resource 11", url: "#creative-arts-11" },
      { name: "Resource 12", url: "#creative-arts-12" },
      { name: "Resource 13", url: "#creative-arts-13" },
      { name: "Resource 14", url: "#creative-arts-14" },
      { name: "Resource 15", url: "#creative-arts-15" },
      { name: "Resource 16", url: "#creative-arts-16" },
      { name: "Resource 17", url: "#creative-arts-17" },
      { name: "Resource 18", url: "#creative-arts-18" },
      { name: "Resource 19", url: "#creative-arts-19" },
      { name: "Resource 20", url: "#creative-arts-20" }
    ]
  },
  rme: {
    id: "pre-6",
    name: "Religious and Moral Education (RME)",
    color: "from-indigo-500 to-blue-400",
    icon: "✝️",
    room: "KG Block A",
    schedule: "Wed 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building moral character through religious teachings, ethical values, and responsible citizenship for young learners.",
    fullDescription: "The Religious and Moral Education program helps children develop strong moral character and ethical values. Through Bible stories, discussions, and practical activities, students learn about kindness, honesty, respect, and responsible behavior.",
    objectives: [
      "Learn religious values and Bible stories",
      "Develop moral character and integrity",
      "Understand the difference between right and wrong",
      "Practice kindness and compassion",
      "Learn to respect others and their beliefs",
      "Develop responsible citizenship"
    ],
    activities: [
      "Bible story time and discussions",
      "Prayer and worship activities",
      "Role-playing moral scenarios",
      "Kindness projects and activities",
      "Character building games",
      "Community service introductions"
    ],
    materials: [
      "Children's Bible and story books",
      "Character education materials",
      "Prayer journals",
      "Visual aids and posters",
      "Moral story picture books"
    ],
    rating: 4.8,
    enrolled: 45,
    resources: [
      { name: "Resource 1", url: "https://www.youtube.com/channel/UCbqRKebu5WkdJEkaga2G-Gg" },
      { name: "Resource 2", url: "https://www.facebook.com/reel/894193840259161" },
      { name: "Resource 3", url: "https://www.nextgenerationimpact.org/impactkids" },
      { name: "Resource 4", url: "https://unite.cefonline.com/" },
      { name: "Resource 5", url: "#rme-5" },
      { name: "Resource 6", url: "#rme-6" },
      { name: "Resource 7", url: "#rme-7" },
      { name: "Resource 8", url: "#rme-8" },
      { name: "Resource 9", url: "#rme-9" },
      { name: "Resource 10", url: "#rme-10" },
      { name: "Resource 11", url: "#rme-11" },
      { name: "Resource 12", url: "#rme-12" },
      { name: "Resource 13", url: "#rme-13" },
      { name: "Resource 14", url: "#rme-14" },
      { name: "Resource 15", url: "#rme-15" },
      { name: "Resource 16", url: "#rme-16" },
      { name: "Resource 17", url: "#rme-17" },
      { name: "Resource 18", url: "#rme-18" },
      { name: "Resource 19", url: "#rme-19" },
      { name: "Resource 20", url: "#rme-20" }
    ]
  }
};

// Individual Subject Page Component
export const KGSubjectPage = () => {
  const navigate = useNavigate();
  const { subject } = useParams();
  
  const subjectData = kgSubjectsData[subject];
  
  if (!subjectData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="bg-white p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-[#002147] mb-4">Subject Not Found</h2>
          <p className="text-gray-600 mb-6">The subject you're looking for doesn't exist.</p>
          <Button 
            onClick={() => navigate("/course-catalog")}
            className="bg-[#FF6B00] hover:bg-[#E65C00]"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Course Catalog
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid={`kg-subject-${subject}`}>
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${subjectData.color} text-white py-12`}>
        <div className="max-w-5xl mx-auto px-4">
          <Button 
            className="mb-4 bg-gradient-to-b from-white/30 to-white/10 hover:from-white/40 hover:to-white/20 text-white border-2 border-white/50 transition-all rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.3)] hover:shadow-[0_2px_0_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
              {subjectData.icon}
            </div>
            <div>
              <Badge className="bg-white/20 text-white mb-2">KG (Kindergarten)</Badge>
              <h1 className="text-3xl md:text-4xl font-bold">{subjectData.name}</h1>
              <p className="text-white/90 mt-2 text-lg">{subjectData.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* External Resources Buttons Section */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-400 py-4">
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
          {/* First Row - 10 buttons */}
          <div className="flex justify-center gap-2 mb-3 min-w-max">
            {subjectData.resources.slice(0, 10).map((resource, index) => {
              const colorStyles = [
                "bg-gradient-to-b from-red-400 to-red-600 shadow-[0_4px_0_0_#991b1b]",
                "bg-gradient-to-b from-orange-400 to-orange-600 shadow-[0_4px_0_0_#c2410c]",
                "bg-gradient-to-b from-amber-400 to-amber-600 shadow-[0_4px_0_0_#b45309]",
                "bg-gradient-to-b from-yellow-400 to-yellow-600 shadow-[0_4px_0_0_#a16207]",
                "bg-gradient-to-b from-lime-400 to-lime-600 shadow-[0_4px_0_0_#4d7c0f]",
                "bg-gradient-to-b from-green-400 to-green-600 shadow-[0_4px_0_0_#15803d]",
                "bg-gradient-to-b from-teal-400 to-teal-600 shadow-[0_4px_0_0_#0f766e]",
                "bg-gradient-to-b from-cyan-400 to-cyan-600 shadow-[0_4px_0_0_#0e7490]",
                "bg-gradient-to-b from-blue-400 to-blue-600 shadow-[0_4px_0_0_#1d4ed8]",
                "bg-gradient-to-b from-indigo-400 to-indigo-600 shadow-[0_4px_0_0_#4338ca]"
              ];
              return (
                <Button 
                  key={index}
                  className={`${colorStyles[index]} text-white font-medium px-4 py-1.5 rounded-full hover:shadow-[0_2px_0_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-sm`}
                  onClick={() => window.open(resource.url, '_blank')}
                  data-testid={`external-resource-btn-${index + 1}`}
                >
                  {resource.name}
                </Button>
              );
            })}
          </div>
          {/* Second Row - 10 buttons */}
          <div className="flex justify-center gap-2 min-w-max">
            {subjectData.resources.slice(10, 20).map((resource, index) => {
              const colorStyles = [
                "bg-gradient-to-b from-violet-400 to-violet-600 shadow-[0_4px_0_0_#6d28d9]",
                "bg-gradient-to-b from-purple-400 to-purple-600 shadow-[0_4px_0_0_#7e22ce]",
                "bg-gradient-to-b from-fuchsia-400 to-fuchsia-600 shadow-[0_4px_0_0_#a21caf]",
                "bg-gradient-to-b from-pink-500 to-pink-700 shadow-[0_4px_0_0_#be185d]",
                "bg-gradient-to-b from-rose-400 to-rose-600 shadow-[0_4px_0_0_#be123c]",
                "bg-gradient-to-b from-red-300 to-red-500 shadow-[0_4px_0_0_#dc2626]",
                "bg-gradient-to-b from-emerald-400 to-emerald-600 shadow-[0_4px_0_0_#059669]",
                "bg-gradient-to-b from-sky-400 to-sky-600 shadow-[0_4px_0_0_#0284c7]",
                "bg-gradient-to-b from-amber-500 to-amber-700 shadow-[0_4px_0_0_#92400e]",
                "bg-gradient-to-b from-teal-500 to-teal-700 shadow-[0_4px_0_0_#115e59]"
              ];
              return (
                <Button 
                  key={index}
                  className={`${colorStyles[index]} text-white font-medium px-4 py-1.5 rounded-full hover:shadow-[0_2px_0_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-sm`}
                  onClick={() => window.open(resource.url, '_blank')}
                  data-testid={`external-resource-btn-${index + 11}`}
                >
                  {resource.name}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="md:col-span-2 space-y-6">
            {/* About This Course */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <BookOpen size={20} /> About This Course
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{subjectData.fullDescription}</p>
              </CardContent>
            </Card>

            {/* Learning Objectives */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Target size={20} /> Learning Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {subjectData.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#FF6B00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Activities */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Lightbulb size={20} /> Learning Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {subjectData.activities.map((activity, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="w-2 h-2 bg-[#FF6B00] rounded-full"></div>
                      <span className="text-gray-700 text-sm">{activity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Materials */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <GraduationCap size={20} /> Learning Materials
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {subjectData.materials.map((material, index) => (
                    <Badge 
                      key={index} 
                      variant="outline"
                      className="py-2 px-3 text-gray-700 border-gray-300"
                    >
                      {material}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Teacher Info */}
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-[#002147] text-white rounded-t-lg">
                <CardTitle className="text-lg">Course Instructor</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="text-center">
                  {subjectData.teacherImage ? (
                    <img src={subjectData.teacherImage} alt={subjectData.teacher} className="w-24 h-24 rounded-full mx-auto border-4 border-[#FF6B00] object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto border-4 border-orange-200 bg-orange-50 flex items-center justify-center">
                      <UserCircle size={40} className="text-orange-300" />
                    </div>
                  )}
                  <h3 className="font-semibold text-[#002147] mt-3">{subjectData.teacher}</h3>
                  <div className="flex items-center justify-center gap-1 text-yellow-500 mt-1">
                    <Star size={14} fill="currentColor" />
                    <span className="text-sm font-medium">{subjectData.rating} rating</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Course Details */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg text-[#002147]">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Schedule</p>
                    <p className="font-medium text-gray-800">{subjectData.schedule}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MapPin size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium text-gray-800">{subjectData.room}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Enrolled</p>
                    <p className="font-medium text-gray-800">{subjectData.enrolled} Students</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <GraduationCap size={18} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level</p>
                    <p className="font-medium text-gray-800">KG (Kindergarten)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Teacher Button */}
            <Button 
              className="w-full bg-[#FF6B00] hover:bg-[#E65C00]"
              onClick={() => navigate("/portal")}
              data-testid="contact-teacher-btn"
            >
              <User size={16} className="mr-2" /> Contact Teacher
            </Button>
          </div>
        </div>
        
        {/* Bottom Back Button */}
        <div className="mt-8 text-center">
          <Button 
            className="bg-gradient-to-b from-[#FF6B00] to-[#E65C00] hover:from-[#FF7B20] hover:to-[#FF6B00] text-white transition-all rounded-full shadow-[0_4px_0_0_#b34700] hover:shadow-[0_2px_0_0_#b34700] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium px-8"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn-bottom"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KGSubjectPage;
